import { useState, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { useToast } from '../../context/ToastContext'
import { getProducts, updateProduct, deleteProduct, getPapConfig, savePapConfig, resetProductToSeedDefault } from '../../data/products'
import { useAuth } from '../../context/AuthContext'
import { BUSINESSES as FALLBACK_BUSINESSES } from '../../data/orderMenu'
import { getAllBusinessImages } from '../../lib/imageService'
import { Plus, Pencil, Save, Trash2, X, Camera, ImageIcon, Upload, RotateCcw } from 'lucide-react'
import AddProductModal from '../ingestion/AddProductModal'
import BulkProductUpload from './BulkProductUpload'
import AddCategoryModal from '../ingestion/AddCategoryModal'   // eslint-disable-line no-unused-vars
import ProductImageManager from './ProductImageManager'

const DEPT_BIZ_KEY_MAP = {
  kitchen: 'desres',
  bakery: 'bakery',
  tuckshop: 'desres',
  yawehs: 'yawehs',
}

const SALE_TYPES_LIST = [
  { value: 'discrete', label: 'Discrete' },
  { value: 'continuous_mirror', label: 'Continuous Mirror' },
  { value: 'continuous_fixed', label: 'Continuous Fixed' },
  { value: 'fixed_unit', label: 'Fixed Unit' },
]

function PapSizingConfig() {
  const { addToast } = useToast()
  const [cfg, setCfg] = useState({ full: 20, half: 12, plate: 10 })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getPapConfig().then(c => setCfg(c))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await savePapConfig({ full: Number(cfg.full), half: Number(cfg.half), plate: Number(cfg.plate) })
      addToast('PAP sizing saved', 'success')
    } catch (err) {
      addToast(`Save failed: ${err.message}`, 'error')
    }
    setSaving(false)
  }

  return (
    <div className="border border-gray-200 rounded p-4 mb-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">PAP Cost Per Meal Size</h3>
      <p className="text-xs text-gray-500 mb-3">These values determine how much pap cost is deducted per meal sold in reconciliation.</p>
      <div className="grid grid-cols-3 gap-3">
        {[['full', 'Full Meal'], ['half', 'Half Meal'], ['plate', 'Plate Meal']].map(([key, label]) => (
          <div key={key}>
            <label className="block text-xs text-gray-500 mb-1">{label}</label>
            <div className="flex items-center gap-1">
              <span className="text-gray-400 text-sm">R</span>
              <input
                type="number" min="0" step="1"
                value={cfg[key] ?? ''}
                onChange={e => setCfg(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-3 bg-terracotta text-white px-4 py-1.5 rounded text-xs font-medium hover:bg-terracotta-dark disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save PAP Sizing'}
      </button>
    </div>
  )
}

export default function ProductsTab({ department }) {
  const { addToast } = useToast()
  const { user } = useAuth()
  const adminEmail = user?.email || 'unknown'
  const [search, setSearch] = useState('')
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [editingCode, setEditingCode] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [confirmReset, setConfirmReset] = useState(null) // product object pending reset
  const [imageItem, setImageItem] = useState(null)
  const [imageCounts, setImageCounts] = useState({})

  // Load image counts for the menu items
  const bizKey = DEPT_BIZ_KEY_MAP[department]
  useEffect(() => {
    getAllBusinessImages(bizKey).then(map => {
      const counts = {}
      map.forEach((urls, itemId) => { counts[itemId] = urls.length })
      setImageCounts(counts)
    }).catch(err => {
      console.warn('[Admin] getAllBusinessImages failed:', err?.message)
      Sentry.captureException(err, { extra: { fn: 'Admin.getAllBusinessImages', bizKey } })
    })
  }, [bizKey, refreshKey])

  const products = useMemo(() => getProducts(department), [department, refreshKey])

  const filtered = products.filter(p =>
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  )

  const isKitchen = department === 'kitchen'

  const startEdit = (p) => {
    setEditingCode(p.code)
    setEditForm({
      name: p.name,
      category: p.category,
      unit: p.unit || 'units',
      qps: p.qps ?? '',
      unitPrice: p.unitPrice ?? '',
      saleType: p.saleType,
      papCost: p.papCost ?? '',
    })
  }

  const saveEdit = async (code) => {
    const updates = {
      name: editForm.name,
      category: editForm.category,
      unit: editForm.unit,
      qps: editForm.qps !== '' ? Number(editForm.qps) : null,
      unitPrice: editForm.unitPrice !== '' ? Number(editForm.unitPrice) : null,
      saleType: editForm.saleType,
    }
    if (isKitchen) updates.papCost = editForm.papCost !== '' ? Number(editForm.papCost) : 0
    try {
      await updateProduct(code, updates, department)
      setEditingCode(null)
      setRefreshKey(k => k + 1)
      addToast(`Updated "${editForm.name}"`, 'success')
    } catch (err) {
      addToast(`Update failed: ${err.message}`, 'error')
    }
  }

  const handleReset = async (code, name) => {
    try {
      const updated = await resetProductToSeedDefault(code, department, adminEmail)
      const priceStr = updated?.unitPrice != null ? `R${Number(updated.unitPrice).toFixed(2)}` : '—'
      addToast(`Reset "${name}" to seed default (${priceStr})`, 'success')
      setConfirmReset(null)
      setRefreshKey(k => k + 1)
    } catch (err) {
      addToast(`Reset failed: ${err.message}`, 'error')
      Sentry.captureException(err, { extra: { fn: 'ProductsTab.handleReset', code, department } })
    }
  }

  const handleDelete = async (code, name) => {
    try {
      await deleteProduct(code, department)
      setConfirmDelete(null)
      setRefreshKey(k => k + 1)
      addToast(`Deleted "${name}"`, 'success')
    } catch (err) {
      addToast(`Delete failed: ${err.message}`, 'error')
    }
  }

  const ef = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }))

  return (
    <div className="space-y-4">
      {isKitchen && <PapSizingConfig />}
      <div className="flex items-center gap-4">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search products..."
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white flex-1 max-w-sm"
        />
        <span className="text-gray-500 text-sm">{filtered.length} products</span>
        <button
          onClick={() => setShowBulkUpload(true)}
          className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 px-4 py-2 rounded-lg text-sm font-medium ml-auto"
        >
          <Upload size={14} /> Bulk Upload
        </button>
        <button
          onClick={() => setShowAddProduct(true)}
          className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={14} /> Add Product
        </button>
      </div>

      <p className="text-xs text-gray-500">Click the pencil icon to edit a product inline. Click the trash icon to delete.</p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-2">Code</th>
              <th className="text-left py-2 px-2">Name</th>
              <th className="text-left py-2 px-2">Category</th>
              <th className="text-left py-2 px-2">Unit</th>
              <th className="text-right py-2 px-2">QPS</th>
              <th className="text-right py-2 px-2">Unit Price</th>
              <th className="text-left py-2 px-2">Sale Type</th>
              {isKitchen && <th className="text-right py-2 px-2">Pap Cost</th>}
              <th className="text-left py-2 px-2">Source</th>
              <th className="text-center py-2 px-2 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const isEditing = editingCode === p.code
              return (
                <tr key={p.code} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="py-2 px-2 font-mono text-xs">{p.code}</td>
                  <td className="py-2 px-2">
                    {isEditing ? (
                      <input value={editForm.name} onChange={e => ef('name', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-full" />
                    ) : p.name}
                  </td>
                  <td className="py-2 px-2 capitalize text-gray-400">
                    {isEditing ? (
                      <input value={editForm.category} onChange={e => ef('category', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-full" />
                    ) : p.category}
                  </td>
                  <td className="py-2 px-2 text-gray-400 text-xs">
                    {isEditing ? (
                      <select value={editForm.unit} onChange={e => ef('unit', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded px-1 py-1 text-xs text-gray-300">
                        <option value="units">Units</option>
                        <option value="rands">Rands</option>
                        <option value="kg">kg</option>
                      </select>
                    ) : (p.unit || '—')}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {isEditing ? (
                      <input type="number" min="0" value={editForm.qps} onChange={e => ef('qps', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-16 text-right" />
                    ) : (p.qps ?? '—')}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {isEditing ? (
                      <input type="number" min="0" step="0.5" value={editForm.unitPrice} onChange={e => ef('unitPrice', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-20 text-right" />
                    ) : (p.unitPrice ? `R${p.unitPrice}` : '—')}
                  </td>
                  <td className="py-2 px-2 text-xs text-gray-400">
                    {isEditing ? (
                      <select value={editForm.saleType} onChange={e => ef('saleType', e.target.value)}
                        className="bg-gray-800 border border-gray-600 rounded px-1 py-1 text-xs text-gray-300">
                        {SALE_TYPES_LIST.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    ) : p.saleType}
                  </td>
                  {isKitchen && (
                    <td className="py-2 px-2 text-right">
                      {isEditing ? (
                        <input type="number" min="0" step="0.01" value={editForm.papCost} onChange={e => ef('papCost', e.target.value)}
                          className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-20 text-right" />
                      ) : (p.papCost ? `R${p.papCost}` : '—')}
                    </td>
                  )}
                  <td className="py-2 px-2">
                    {p.custom
                      ? <span className="text-xs bg-brand-900 text-brand-400 px-1.5 py-0.5 rounded">Custom</span>
                      : <span className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">Seed</span>
                    }
                  </td>
                  <td className="py-2 px-2 text-center">
                    {isEditing ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => saveEdit(p.code)} className="text-green-400 hover:text-green-300" title="Save">
                          <Save size={14} />
                        </button>
                        <button onClick={() => setEditingCode(null)} className="text-gray-500 hover:text-gray-300" title="Cancel">
                          <X size={14} />
                        </button>
                      </div>
                    ) : confirmDelete === p.code ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleDelete(p.code, p.name)} className="text-red-400 hover:text-red-300 text-xs">Yes</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-gray-500 hover:text-gray-300 text-xs">No</button>
                      </div>
                    ) : confirmReset === p.code ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleReset(p.code, p.name)} className="text-amber-400 hover:text-amber-300 text-xs">Reset</button>
                        <button onClick={() => setConfirmReset(null)} className="text-gray-500 hover:text-gray-300 text-xs">No</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(p)} className="text-gray-500 hover:text-blue-400" title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setConfirmReset(p.code)} className="text-gray-500 hover:text-amber-400" title="Reset to seed default">
                          <RotateCcw size={13} />
                        </button>
                        <button onClick={() => setConfirmDelete(p.code)} className="text-gray-500 hover:text-red-400" title="Delete">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ─── Menu Images Section ─── */}
      <div className="mt-8 space-y-3">
        <div className="flex items-center gap-2">
          <ImageIcon size={16} className="text-gray-400" />
          <h3 className="text-sm font-bold text-white">Menu Images</h3>
          <span className="text-xs text-gray-500">Upload images for customer-facing menu items</span>
        </div>
        {(() => {
          const biz = FALLBACK_BUSINESSES.find(b => b.key === bizKey)
          if (!biz) return <p className="text-xs text-gray-600">No menu items found for this department</p>
          return biz.categories.filter(c => !c.externalLink).map(cat => (
            <div key={cat.key} className="space-y-1">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{cat.label}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {cat.items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => setImageItem({ ...item, _bizKey: bizKey })}
                    className="flex items-center gap-2 bg-gray-800/50 border border-gray-700/50 hover:border-brand-600 rounded px-3 py-2 text-left transition-colors group"
                  >
                    <Camera size={14} className="text-gray-600 group-hover:text-brand-400 shrink-0" />
                    <span className="text-xs text-gray-300 truncate flex-1">{item.name}</span>
                    {imageCounts[item.id] > 0 && (
                      <span className="text-[10px] bg-brand-600 text-white px-1.5 py-0.5 rounded-full shrink-0">
                        {imageCounts[item.id]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))
        })()}
      </div>

      {showAddProduct && (
        <AddProductModal
          department={department}
          onClose={() => setShowAddProduct(false)}
          onProductAdded={() => setRefreshKey(k => k + 1)}
        />
      )}

      {showBulkUpload && (
        <BulkProductUpload
          department={department}
          onClose={() => setShowBulkUpload(false)}
          onProductsAdded={() => setRefreshKey(k => k + 1)}
        />
      )}

      {imageItem && (
        <ProductImageManager
          businessKey={imageItem._bizKey}
          item={imageItem}
          onClose={() => { setImageItem(null); setRefreshKey(k => k + 1) }}
        />
      )}
    </div>
  )
}
